import React, { FC, ReactElement, useRef, useState } from "react";
import { Grid, Input, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { translate } from "../../../../translate/localization";
import regexTester from "../../../../../services/regexFunctions";

import useStyles from "./styles";
import CobeeLink from "../../../../../app/components/CobeeLink";

interface EmailSectionProps {
  email: string;
  emailList?: string[];
  handleSave?: (email: string) => void;
  handleEdit?: ({
    oldEmail,
    email,
  }: {
    oldEmail: string;
    email: string;
  }) => void;
  handleDelete?: (email: string) => void;
}

const EmailSection: FC<EmailSectionProps> = ({
  email = "",
  emailList = [],
  handleSave = () => {},
  handleEdit = () => {},
  handleDelete = () => {},
}): ReactElement => {
  const { container, label, inputStyle, linkContainer, linkStyle, errorStyle } =
    useStyles();
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [formEmail, setFormEmail] = useState(email || "");

  const inputRef = useRef<HTMLInputElement>(null);

  const edit = () => {
    setEditing(true);
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
    setFormEmail(email);
  };

  const saveChanges = () => {
    if (!regexTester({ value: formEmail, type: "email" })) {
      return setError(
        `${translate("common-messages.email")} ${translate(
          "common-messages.not-valid"
        )}`
      );
    }
    if (emailList.some((e) => e === formEmail)) {
      return setError(`${translate("employees-section.duplicated-email")}`);
    }
    if (editing) {
      handleEdit({ oldEmail: email, email: formEmail });
    } else {
      handleSave(formEmail);
    }
    if (inputRef && inputRef.current) {
      inputRef.current.blur();
    }
    setFormEmail("");
    return setEditing(false);
  };

  const deleteEmail = () => {
    handleDelete(formEmail);
    setFormEmail("");
  };

  const handleChange = (value) => {
    if (error) setError("");
    setFormEmail(value);
  };

  const emptyInput = formEmail === "";
  return (
    <Grid className={container}>
      <Grid className={linkContainer}>
        <Typography className={label}>
          {translate("common-messages.email")}
        </Typography>
        <FontAwesomeIcon
          icon="trash"
          className={`${label} ${linkStyle}`}
          onClick={deleteEmail}
        />
      </Grid>
      <Grid className={linkContainer}>
        <Input
          type="text"
          className={`${label} ${inputStyle}`}
          placeholder="admin@test.io"
          value={formEmail}
          inputRef={inputRef}
          onFocus={() => email === formEmail && edit}
          onChange={(e) => handleChange(e.target.value)}
          error={error !== ""}
        />
        {email !== formEmail || emptyInput ? (
          <CobeeLink
            disabled={error !== "" || emptyInput}
            handleClick={() => formEmail !== "" && saveChanges()}
            linkText={`${translate("actions.save")}`}
          />
        ) : (
          <CobeeLink
            handleClick={edit}
            linkText={`${translate("actions.edit")}`}
          />
        )}
      </Grid>
      {error && (
        <Typography className={`${label} ${errorStyle}`}>{error}</Typography>
      )}
    </Grid>
  );
};

export default EmailSection;
